import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import TrackAnalyticsModal from '../../components/TrackAnalyticsModal';
import { 
    TrendingUp, 
    TrendingDown,
    DollarSign, 
    Building2, 
    Music2, 
    Globe,
    Users,
    Target,
    Zap,
    BarChart3,
    PieChart,
    Activity,
    ArrowUpRight,
    ArrowDownRight,
    Loader2
} from 'lucide-react';
import { 
    AreaChart, Area, BarChart, Bar, LineChart, Line, XAxis, YAxis, 
    CartesianGrid, Tooltip, ResponsiveContainer, PieChart as RechartsPie, 
    Pie, Cell, ComposedChart, Legend, RadarChart, PolarGrid, PolarAngleAxis,
    PolarRadiusAxis, Radar
} from 'recharts';
import { formatCurrency, formatCurrencyFull, formatNumber, formatMonth } from '../../lib/utils';
import api from '../../lib/api';
import { useTheme } from '../../context/ThemeContext';

const COLORS = ['#7C3AED', '#00FF9D', '#CCFF00', '#00F2EA', '#FFB700', '#FF4444', '#A78BFA', '#34D399'];

// Round growth percentages to a max of 1 decimal for clean display (e.g., 12.345% -> 12.3%)
const roundOne = (val) => {
    const n = Number(val);
    if (!Number.isFinite(n)) return 0;
    return Math.round(n * 10) / 10;
};

// Theme-aware chart colors
const useChartColors = () => {
    const { isDark } = useTheme();
    return {
        grid: isDark ? 'rgba(148,163,184,0.22)' : '#E2E8F0',
        axis: isDark ? '#CBD5E1' : '#334155',
        tooltipBg: isDark ? '#0F172A' : '#FFFFFF',
        tooltipBorder: isDark ? '#334155' : '#CBD5E1',
        text: isDark ? '#F8FAFC' : '#0F172A',
        mutedText: isDark ? '#CBD5E1' : '#475569'
    };
};

const formatChartCurrency = (value) => {
    const n = Number(value || 0);
    if (!Number.isFinite(n)) return '₹0';
    return `₹${new Intl.NumberFormat('en-IN', {
        notation: 'compact',
        maximumFractionDigits: 1
    }).format(n)}`;
};

const formatChartMonth = (value) => {
    if (!value) return '';
    const str = String(value);
    if (/^\d{4}-\d{2}$/.test(str)) {
        const [year, month] = str.split('-');
        return new Date(Number(year), Number(month) - 1).toLocaleDateString('en-IN', {
            month: 'short',
            year: '2-digit'
        });
    }
    return str;
};

const FounderDashboard = () => {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [timeRange, setTimeRange] = useState('12m');
    const [viewMode, setViewMode] = useState('cash_flow'); // 'performance' or 'cash_flow'
    const [labelFilter, setLabelFilter] = useState('all');
    const [labelsList, setLabelsList] = useState([]);
    const [selectedTrack, setSelectedTrack] = useState(null);
    const [trackModalOpen, setTrackModalOpen] = useState(false);
    const chartColors = useChartColors();
    const axisTick = { fill: chartColors.axis, fontSize: 12, fontWeight: 500 };
    const tooltipStyle = {
        backgroundColor: chartColors.tooltipBg,
        border: `1px solid ${chartColors.tooltipBorder}`,
        borderRadius: '10px',
        color: chartColors.text,
        boxShadow: '0 12px 30px rgba(15, 23, 42, 0.16)'
    };
    const tooltipLabelStyle = { color: chartColors.text, fontWeight: 700 };
    const tooltipItemStyle = { color: chartColors.text };
    const legendStyle = { color: chartColors.text, fontSize: 12, fontWeight: 600 };
    const chartMargin = { top: 16, right: 28, left: 12, bottom: 12 };

    // Fetch labels list once for the filter dropdown
    useEffect(() => {
        api.get('/labels').then(res => setLabelsList(res.data || [])).catch(() => setLabelsList([]));
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const monthType = viewMode === 'performance' ? 'transaction_month' : 'reporting_month';
                let url = `/analytics/founder?time_range=${timeRange}&month_type=${monthType}`;
                if (labelFilter !== 'all') url += `&label_id=${labelFilter}`;
                const response = await api.get(url);
                setData(response.data);
            } catch (error) {
                console.error('Failed to fetch founder analytics:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [timeRange, viewMode, labelFilter]);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-[50vh]">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
        );
    }

    return (
        <div className="space-y-8" data-testid="founder-dashboard">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-heading font-bold tracking-tight">Founder Analytics</h1>
                    <p className="text-muted-foreground">
                        {viewMode === 'performance' 
                            ? 'Performance view — grouped by transaction month (when streams happened)' 
                            : 'Cash flow view — grouped by reporting month (when platforms paid us)'}
                    </p>
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                    {/* View Mode Toggle */}
                    <div className="flex items-center gap-1 p-1 bg-muted/30 rounded-lg" data-testid="founder-view-toggle">
                        <button
                            onClick={() => setViewMode('performance')}
                            data-testid="founder-toggle-performance"
                            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                                viewMode === 'performance'
                                    ? 'bg-primary text-primary-foreground shadow-sm'
                                    : 'text-muted-foreground hover:text-foreground'
                            }`}
                        >
                            Performance
                        </button>
                        <button
                            onClick={() => setViewMode('cash_flow')}
                            data-testid="founder-toggle-cashflow"
                            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                                viewMode === 'cash_flow'
                                    ? 'bg-primary text-primary-foreground shadow-sm'
                                    : 'text-muted-foreground hover:text-foreground'
                            }`}
                        >
                            Cash Flow
                        </button>
                    </div>
                    <Select value={timeRange} onValueChange={setTimeRange}>
                        <SelectTrigger className="w-40 bg-card border-border/50" data-testid="founder-time-range">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="3m">Last 3 months</SelectItem>
                            <SelectItem value="6m">Last 6 months</SelectItem>
                            <SelectItem value="12m">Last 12 months</SelectItem>
                            <SelectItem value="all">All time</SelectItem>
                        </SelectContent>
                    </Select>
                    {/* Label filter — scope analytics to a single label */}
                    <Select value={labelFilter} onValueChange={setLabelFilter}>
                        <SelectTrigger className="w-52 bg-card border-border/50" data-testid="founder-label-filter">
                            <SelectValue placeholder="All Labels" />
                        </SelectTrigger>
                        <SelectContent className="max-h-72 overflow-y-auto">
                            <SelectItem value="all">All Labels</SelectItem>
                            {labelsList.map((l) => (
                                <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            </div>

            {/* KPI Cards Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
                <KPICard
                    title="Gross Revenue"
                    value={formatCurrency(data?.kpis?.gross_revenue_inr ?? data?.kpis?.total_revenue ?? 0)}
                    change={data?.kpis?.revenue_growth || 0}
                    icon={DollarSign}
                    color="secondary"
                    data-testid="kpi-gross-revenue"
                />
                <KPICard
                    title="Net Revenue"
                    value={formatCurrency(data?.kpis?.net_revenue_inr || 0)}
                    change={data?.kpis?.revenue_growth || 0}
                    icon={DollarSign}
                    color="primary"
                    data-testid="kpi-net-revenue"
                />
                <KPICard
                    title="Active Labels"
                    value={data?.kpis?.total_labels || 0}
                    change={data?.kpis?.labels_growth || 0}
                    icon={Building2}
                    color="primary"
                />
                <KPICard
                    title="Total Streams"
                    value={formatNumber(data?.kpis?.total_streams || 0)}
                    change={data?.kpis?.streams_growth || 0}
                    icon={Activity}
                    color="accent"
                />
                <KPICard
                    title="Catalog Size"
                    value={formatNumber(data?.kpis?.total_songs || 0)}
                    change={data?.kpis?.songs_growth || 0}
                    icon={Music2}
                    color="cyan"
                />
            </div>

            {/* Revenue Growth Chart */}
            <Card className="bg-card/50 border-border/40">
                <CardHeader>
                    <div className="flex items-center justify-between">
                        <div>
                            <CardTitle className="font-heading flex items-center gap-2">
                                <TrendingUp className="w-5 h-5 text-secondary" />
                                Revenue Growth Trend
                            </CardTitle>
                            <CardDescription>Monthly gross revenue (INR) with month-over-month growth</CardDescription>
                        </div>
                    </div>
                </CardHeader>
                <CardContent>
                    <div className="h-[400px]">
                        <ResponsiveContainer width="100%" height="100%">
                            <ComposedChart data={data?.revenue_trend || []} margin={chartMargin}>
                                <defs>
                                    <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#7C3AED" stopOpacity={0.3}/>
                                        <stop offset="95%" stopColor="#7C3AED" stopOpacity={0}/>
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
                                <XAxis dataKey="month" tickFormatter={formatChartMonth} stroke={chartColors.axis} tick={axisTick} tickLine={false} axisLine={{ stroke: chartColors.grid }} tickMargin={10} />
                                <YAxis yAxisId="left" stroke={chartColors.axis} tick={axisTick} tickLine={false} axisLine={{ stroke: chartColors.grid }} tickFormatter={formatChartCurrency} width={72} />
                                <YAxis yAxisId="right" orientation="right" stroke={chartColors.axis} tick={axisTick} tickLine={false} axisLine={{ stroke: chartColors.grid }} tickFormatter={(value) => `${value}%`} width={52} />
                                <Tooltip 
                                    contentStyle={tooltipStyle}
                                    labelStyle={tooltipLabelStyle}
                                    itemStyle={tooltipItemStyle}
                                    formatter={(value, name) => [
                                        name === 'growth' ? `${value}%` : formatChartCurrency(value),
                                        name === 'revenue' ? 'Revenue' : 'Growth %'
                                    ]}
                                />
                                <Legend wrapperStyle={legendStyle} />
                                <Area yAxisId="left" type="monotone" dataKey="revenue" name="Revenue" stroke="#7C3AED" fillOpacity={1} fill="url(#colorRevenue)" />
                                <Bar yAxisId="right" dataKey="growth" name="Growth %" fill="#CCFF00" radius={[4, 4, 0, 0]} opacity={0.7} />
                            </ComposedChart>
                        </ResponsiveContainer>
                    </div>
                </CardContent>
            </Card>

            {/* Two Column Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Label Performance Comparison */}
                <Card className="bg-card/50 border-border/40">
                    <CardHeader>
                        <CardTitle className="font-heading flex items-center gap-2">
                            <Building2 className="w-5 h-5 text-primary" />
                            Label Performance
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="h-[300px]">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={data?.label_performance || []} layout="vertical" margin={{ top: 8, right: 28, left: 20, bottom: 8 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
                                    <XAxis type="number" stroke={chartColors.axis} tick={axisTick} tickLine={false} axisLine={{ stroke: chartColors.grid }} tickFormatter={formatChartCurrency} />
                                    <YAxis dataKey="name" type="category" width={132} stroke={chartColors.axis} tick={axisTick} tickLine={false} axisLine={false} />
                                    <Tooltip 
                                        contentStyle={tooltipStyle}
                                        labelStyle={tooltipLabelStyle}
                                        itemStyle={tooltipItemStyle}
                                        formatter={(value) => formatCurrencyFull(value)}
                                    />
                                    <Bar dataKey="revenue" fill="#7C3AED" radius={[0, 4, 4, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </CardContent>
                </Card>

                {/* Platform Market Share */}
                <Card className="bg-card/50 border-border/40">
                    <CardHeader>
                        <CardTitle className="font-heading flex items-center gap-2">
                            <PieChart className="w-5 h-5 text-secondary" />
                            Platform Market Share
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="h-[300px]">
                            <ResponsiveContainer width="100%" height="100%">
                                <RechartsPie>
                                    <Pie
                                        data={data?.platform_share || []}
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={60}
                                        outerRadius={100}
                                        paddingAngle={5}
                                        dataKey="revenue"
                                        nameKey="platform"
                                    >
                                        {(data?.platform_share || []).map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                        ))}
                                    </Pie>
                                    <Tooltip 
                                        contentStyle={tooltipStyle}
                                        labelStyle={tooltipLabelStyle}
                                        itemStyle={tooltipItemStyle}
                                        formatter={(value) => formatCurrencyFull(value)} 
                                    />
                                    <Legend wrapperStyle={legendStyle} />
                                </RechartsPie>
                            </ResponsiveContainer>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Geographic Distribution */}
            <Card className="bg-card/50 border-border/40">
                <CardHeader>
                    <CardTitle className="font-heading flex items-center gap-2">
                        <Globe className="w-5 h-5 text-accent" />
                        Geographic Revenue Distribution
                    </CardTitle>
                    <CardDescription>Revenue breakdown by country/region</CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="h-[420px]">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart
                                data={data?.geographic_distribution || []}
                                layout="vertical"
                                margin={{ top: 8, right: 28, left: 24, bottom: 8 }}
                            >
                                <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
                                <XAxis type="number" stroke={chartColors.axis} tick={axisTick} tickLine={false} axisLine={{ stroke: chartColors.grid }} tickFormatter={formatChartCurrency} />
                                <YAxis dataKey="country" type="category" width={180} stroke={chartColors.axis} tick={axisTick} tickLine={false} axisLine={false} />
                                <Tooltip 
                                    contentStyle={tooltipStyle}
                                    labelStyle={tooltipLabelStyle}
                                    itemStyle={tooltipItemStyle}
                                    formatter={(value, name) => [formatCurrencyFull(value), 'Revenue']}
                                />
                                <Bar dataKey="revenue" fill="#00FF9D" radius={[0, 4, 4, 0]}>
                                    {(data?.geographic_distribution || []).map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </CardContent>
            </Card>

            {/* Business Health Metrics */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Invoice Status */}
                <Card className="bg-card/50 border-border/40">
                    <CardHeader>
                        <CardTitle className="font-heading">Invoice Pipeline</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-4">
                            <div className="flex justify-between items-center">
                                <span className="text-muted-foreground">Pending</span>
                                <span className="font-mono text-yellow-500">{formatCurrency(data?.invoices?.pending_amount || 0)}</span>
                            </div>
                            <div className="w-full bg-muted rounded-full h-2">
                                <div className="bg-yellow-500 h-2 rounded-full" style={{ width: `${data?.invoices?.pending_percent || 0}%` }} />
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-muted-foreground">Approved</span>
                                <span className="font-mono text-secondary">{formatCurrency(data?.invoices?.approved_amount || 0)}</span>
                            </div>
                            <div className="w-full bg-muted rounded-full h-2">
                                <div className="bg-secondary h-2 rounded-full" style={{ width: `${data?.invoices?.approved_percent || 0}%` }} />
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-muted-foreground">Paid</span>
                                <span className="font-mono text-primary">{formatCurrency(data?.invoices?.paid_amount || 0)}</span>
                            </div>
                            <div className="w-full bg-muted rounded-full h-2">
                                <div className="bg-primary h-2 rounded-full" style={{ width: `${data?.invoices?.paid_percent || 0}%` }} />
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Top Performers */}
                <Card className="bg-card/50 border-border/40">
                    <CardHeader>
                        <CardTitle className="font-heading">Top 10 Performing Tracks</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-3 max-h-[520px] overflow-y-auto pr-1">
                            {(data?.top_tracks || []).slice(0, 10).map((track, i) => (
                                <div 
                                    key={i} 
                                    className="flex items-center gap-4 p-3 rounded-xl bg-muted/20 hover:bg-muted/30 transition-colors group cursor-pointer"
                                    onClick={() => {
                                        setSelectedTrack(track.title);
                                        setTrackModalOpen(true);
                                    }}
                                >
                                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-primary to-secondary flex items-center justify-center text-white font-bold shrink-0">
                                        {i + 1}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="font-medium truncate group-hover:text-primary transition-colors">
                                            {track.title}
                                        </p>
                                        <p className="text-sm text-muted-foreground truncate">
                                            {track.artist} • {formatNumber(track.streams)} streams
                                        </p>
                                    </div>
                                    <div className="text-right shrink-0">
                                        <p className="font-mono font-bold text-secondary">
                                            {formatCurrency(track.revenue, 'INR')}
                                        </p>
                                        <p className="text-xs text-muted-foreground">revenue</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>

                {/* Growth Metrics */}
                <Card className="bg-card/50 border-border/40">
                    <CardHeader>
                        <CardTitle className="font-heading">Growth Metrics</CardTitle>
                        <CardDescription>
                            MoM compares the latest month with the previous month. QoQ compares the latest 3 months with the preceding 3 months. YoY compares the latest month with the same month last year.
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-4">
                            <MetricRow
                                label="MoM Revenue Growth"
                                value={data?.growth?.mom_revenue == null ? 'N/A' : `${roundOne(data.growth.mom_revenue)}%`}
                                positive={data?.growth?.mom_revenue == null ? undefined : data.growth.mom_revenue > 0}
                            />
                            <MetricRow
                                label="QoQ Revenue Growth"
                                value={data?.growth?.qoq_revenue == null ? 'N/A' : `${roundOne(data.growth.qoq_revenue)}%`}
                                positive={data?.growth?.qoq_revenue == null ? undefined : data.growth.qoq_revenue > 0}
                            />
                            <MetricRow
                                label="YoY Revenue Growth"
                                value={data?.growth?.yoy_revenue == null ? 'N/A' : `${roundOne(data.growth.yoy_revenue)}%`}
                                positive={data?.growth?.yoy_revenue == null ? undefined : data.growth.yoy_revenue > 0}
                            />
                            <MetricRow label="Avg Revenue per Label" value={formatCurrency(data?.growth?.avg_revenue_per_label || 0)} />
                            <MetricRow label="Avg Revenue per Song" value={formatCurrency(data?.growth?.avg_revenue_per_song || 0)} />
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Forecasting Section */}
            <Card className="bg-gradient-to-br from-primary/10 to-secondary/10 border-primary/20">
                <CardHeader>
                    <CardTitle className="font-heading flex items-center gap-2">
                        <Target className="w-5 h-5 text-primary" />
                        Revenue Forecast (Next 6 Months)
                    </CardTitle>
                    <CardDescription>
                        Trend-based estimate using up to the latest 6 consecutive months of actual revenue. Forecasts are directional estimates and not guaranteed future revenue.
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="h-[300px]">
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={data?.forecast || []} margin={chartMargin}>
                                <defs>
                                    <linearGradient id="colorForecast" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#00FF9D" stopOpacity={0.3}/>
                                        <stop offset="95%" stopColor="#00FF9D" stopOpacity={0}/>
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
                                <XAxis dataKey="month" tickFormatter={formatChartMonth} stroke={chartColors.axis} tick={axisTick} tickLine={false} axisLine={{ stroke: chartColors.grid }} tickMargin={10} />
                                <YAxis stroke={chartColors.axis} tick={axisTick} tickLine={false} axisLine={{ stroke: chartColors.grid }} tickFormatter={formatChartCurrency} width={72} />
                                <Tooltip 
                                    contentStyle={tooltipStyle}
                                    labelStyle={tooltipLabelStyle}
                                    itemStyle={tooltipItemStyle}
                                    formatter={(value) => [formatCurrencyFull(value), 'Projected Revenue']}
                                />
                                <Area type="monotone" dataKey="projected" stroke="#00FF9D" fillOpacity={1} fill="url(#colorForecast)" strokeWidth={2} />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                </CardContent>
            </Card>
            
            {/* Track Analytics Modal */}
            <TrackAnalyticsModal 
                open={trackModalOpen}
                onOpenChange={setTrackModalOpen}
                trackName={selectedTrack}
            />
        </div>
    );
};

// KPI Card Component
const KPICard = ({ title, value, change, icon: Icon, color }) => {
    const isPositive = change >= 0;
    const colorClasses = {
        primary: 'bg-primary/20 text-primary',
        secondary: 'bg-secondary/20 text-secondary',
        accent: 'bg-accent/20 text-accent',
        cyan: 'bg-cyan-500/20 text-cyan-500'
    };

    return (
        <Card className="bg-card/50 border-border/40">
            <CardContent className="p-6">
                <div className="flex items-start justify-between">
                    <div className="space-y-2">
                        <p className="text-sm text-muted-foreground">{title}</p>
                        <p className="text-3xl font-heading font-bold">{value}</p>
                        {change !== undefined && (
                            <div className={`flex items-center gap-1 text-sm ${isPositive ? 'text-secondary' : 'text-destructive'}`}>
                                {isPositive ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
                                <span>{Math.abs(roundOne(change))}%</span>
                                <span className="text-muted-foreground">vs last period</span>
                            </div>
                        )}
                    </div>
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${colorClasses[color]}`}>
                        <Icon className="w-6 h-6" />
                    </div>
                </div>
            </CardContent>
        </Card>
    );
};

// Metric Row Component
const MetricRow = ({ label, value, positive }) => (
    <div className="flex justify-between items-center">
        <span className="text-sm text-muted-foreground">{label}</span>
        <span className={`font-mono font-medium ${positive === true ? 'text-secondary' : positive === false ? 'text-destructive' : ''}`}>
            {value}
        </span>
    </div>
);

export default FounderDashboard;
